import { Link } from "react-router-dom";
import { Pencil, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { CARD_TITLE } from "@/lib/typography";
import { TagLabel } from "@/components/TagLabel";
import { Button } from "@/components/ui/button";
import { SectionHeader } from "@/components/shared/SectionHeader";
import { useTabNavigation } from "@/hooks/useTabNavigation";
import { HOME_LAYOUT } from "@/routes";
import { CARD_COUNT, type PlaceablePage } from "./homeLayout";
import { PAGE_INFO, type PageInfo } from "./pages";
import { useHomeLayout } from "./useHomeLayout";
import { useHomePreviews, type Cover } from "./useHomePreviews";

function PageCover({ cover, icon: Icon }: { cover: Cover; icon: LucideIcon }) {
  if (cover && "tags" in cover) {
    return (
      <span className="navigation-cover navigation-cover--tags">
        {cover.tags.map(tag => (
          <span key={tag.name} className="navigation-cover-tag">
            <TagLabel name={tag.name} color={tag.color} size="xs" truncate />
          </span>
        ))}
      </span>
    );
  }
  if (cover && "bubbles" in cover) {
    return (
      <span className="navigation-cover navigation-cover--chat">
        {cover.bubbles.map(text => (
          <span key={text} className="navigation-cover-bubble">{text}</span>
        ))}
      </span>
    );
  }
  const images = cover?.images ?? [];
  return (
    <span className="navigation-cover">
      {images.map((url, index) => (
        <img
          key={url}
          src={url}
          alt=""
          loading="lazy"
          onError={event => { event.currentTarget.hidden = true; }}
          className={cn(
            "h-full w-full object-cover",
            images.length === 1 && "col-span-2 row-span-2",
            images.length === 2 && "row-span-2",
            images.length === 3 && index === 0 && "row-span-2",
          )}
        />
      ))}
      {images.length === 0 && !cover?.caption && (
        <span className="col-span-2 row-span-2 flex items-center justify-center">
          <Icon className="h-8 w-8 text-muted-foreground" aria-hidden="true" />
        </span>
      )}
      {cover?.caption && (
        <span className={cn("navigation-cover-caption", images.length === 0 && "navigation-cover-caption--plain")}>
          <span className="navigation-cover-caption-title">{cover.caption.title}</span>
          {cover.caption.detail && <span>{cover.caption.detail}</span>}
        </span>
      )}
      {cover?.credit && <span className="navigation-cover-credit">{cover.credit}</span>}
    </span>
  );
}

/**
 * The pages placed on Home (StashesPage.swift `StashesSections`), under "Shortcuts"
 * with Customize. In one column the first two are cards and the rest shortcut tiles, four
 * across; beside the rest of Home (`pane`, a fifth of the window) every page is a card,
 * two by two once the pane fits two titles (from `2xl`), one by one before. Pages the
 * web doesn't have yet are skipped.
 */
export function HomePages({ pane = false, className }: { pane?: boolean; className?: string }) {
  const { layout } = useHomeLayout();
  const { handleTabClick } = useTabNavigation();
  const available = (pages: PlaceablePage[]) =>
    pages.flatMap(page => (PAGE_INFO[page] ? [{ page, info: PAGE_INFO[page] as PageInfo }] : []));
  const cards = available(pane ? layout.home : layout.home.slice(0, CARD_COUNT));
  const shortcuts = pane ? [] : available(layout.home.slice(CARD_COUNT));
  const previews = useHomePreviews(cards.map(card => card.page));

  const open = (info: PageInfo) => (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (info.tab) handleTabClick(info.tab, event);
  };

  // Everything can come off Home; then there's no Shortcuts section at all.
  if (cards.length === 0 && shortcuts.length === 0) return null;

  return (
    <section className={cn("navigation-shortcuts", className)} aria-label="Shortcuts">
      <SectionHeader
        title="Shortcuts"
        className="min-w-0 flex-1"
        action={
          <Button asChild variant="ghost" size="icon-control">
            <Link to={HOME_LAYOUT()} aria-label="Customize Home" title="Customize Home">
              <Pencil aria-hidden="true" />
            </Link>
          </Button>
        }
      />
      <div className={cn("navigation-cards", pane && "navigation-cards--pane")}>
        {cards.map(({ page, info }) => {
          const Icon = info.icon;
          const preview = previews[page];
          return (
            <Link key={page} to={info.to} onClick={open(info)} className="group navigation-card">
              <PageCover cover={preview?.cover ?? null} icon={Icon} />
              <span className="navigation-card-copy">
                <span className={cn(CARD_TITLE, "navigation-card-title")}>
                  <Icon aria-hidden="true" />
                  <span className="truncate">{info.title}</span>
                </span>
                <span className="navigation-card-caption">{preview?.line}</span>
              </span>
            </Link>
          );
        })}
      </div>
      {shortcuts.length > 0 && (
        <div className="navigation-tiles">
          {shortcuts.map(({ page, info }) => {
            const Icon = info.icon;
            return (
              <Link
                key={page}
                to={info.to}
                onClick={open(info)}
                className="group navigation-tile"
              >
                <span className="navigation-tile-cover">
                  <Icon aria-hidden="true" />
                </span>
                <span className="navigation-tile-label">{info.title}</span>
              </Link>
            );
          })}
        </div>
      )}
    </section>
  );
}
